import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const DEFAULTS = { textSize: "normal", contrast: false, reduceMotion: false, lineSpacing: "normal", simple: false };
const KEY = "aptiport-a11y";
const A11yCtx = createContext(null);

function load() {
  try { return { ...DEFAULTS, ...JSON.parse(sessionStorage.getItem(KEY) || localStorage.getItem(KEY) || "{}") }; } catch { return DEFAULTS; }
}

export function A11yProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(load);
  const [open, setOpen] = useState(false);
  const syncedFor = useRef(null);

  useEffect(() => {
    const h = document.documentElement;
    h.classList.toggle("ts-lg", prefs.textSize === "large");
    h.classList.toggle("ts-xl", prefs.textSize === "xlarge");
    h.classList.toggle("hc", !!prefs.contrast);
    h.classList.toggle("rm", !!prefs.reduceMotion);
    h.classList.toggle("ls-relaxed", prefs.lineSpacing === "relaxed");
    h.classList.toggle("ls-wide", prefs.lineSpacing === "wide");
    h.classList.toggle("simple", !!prefs.simple);
    localStorage.setItem(KEY, JSON.stringify(prefs));
  }, [prefs]);

  useEffect(() => {
    if (user && syncedFor.current !== user.id) {
      syncedFor.current = user.id;
      if (user.a11y && Object.keys(user.a11y).length) setPrefs({ ...DEFAULTS, ...user.a11y });
    }
    if (user === false) syncedFor.current = null;
  }, [user]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && e.shiftKey && e.code === "KeyA") { e.preventDefault(); setOpen((o) => !o); }
      if (e.altKey && e.shiftKey && e.code === "KeyS") { const el = document.getElementById("page-search"); if (el) { e.preventDefault(); el.focus(); } }
      if (e.altKey && e.shiftKey && e.code === "KeyM") { const el = document.getElementById("main"); if (el) { e.preventDefault(); el.focus(); } }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const save = (next) => {
    setPrefs(next);
    if (user) api.put("/me/a11y", next).catch(() => {});
  };
  const update = (k, v) => save({ ...prefs, [k]: v });
  const reset = () => save(DEFAULTS);

  return <A11yCtx.Provider value={{ prefs, update, reset, open, setOpen }}>{children}</A11yCtx.Provider>;
}

export const useA11y = () => useContext(A11yCtx);
